import React, { useContext, useEffect, useState } from 'react';
import { DContext } from '../../../context/DataContext';
import LoadingPage from '../Loading';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatContact } from '../../../utils/contact';

function ViewBooking() {
  const { t } = useTranslation();
  const { BeURL, setLoader } = useContext(DContext);
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (BeURL && id) {
      setLoading(true);
      setNotFound(false);
      setLoader(true);
      fetch(`${BeURL}/fetch-booking/${id}`, { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => {
          setLoader(false);
          setLoading(false);
          if (data.success) {
            setBooking(data.booking);
            setNotFound(false);
          } else {
            setBooking(null);
            setNotFound(true);
            alert(t('adminCommon.requestFailed'));
          }
        })
        .catch((err) => {
          setLoader(false);
          setLoading(false);
          console.error("Error fetching booking:", err);
          setNotFound(true);
          alert(t('view-booking.fetchError'));
        });
    }
  }, [BeURL, id, setLoader, t]);

  const renderCategory = (pkg) => {
    if (pkg === "price") {
      return t("packageDetails.fullDay")
    }
    else if (pkg === "halfPrice") {
      return t("packageDetails.halfDay")
    }
    else if (pkg === "speedBoat") {
      return t("packageDetails.speedBoat")
    }
    else if (pkg === "longTail") {
      return t("packageDetails.longtailBoat")
    }
  }


  if (loading) {
    return <LoadingPage />;
  }

  if (notFound || !booking) {
    return (
      <div className="mx-auto my-6 max-w-2xl rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_16px_38px_rgba(15,23,42,0.08)]">
        <h3 className="text-xl font-bold text-slate-900">{t('view-booking.heading')}</h3>
        <p className="mt-2 text-sm text-slate-600">{t('view-booking.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto my-2 max-w-5xl">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_16px_38px_rgba(15,23,42,0.08)]">
        <div className="bg-[#1739bd] px-5 py-4 text-white">
          <h3 className="text-xl font-bold tracking-tight">
            {t('view-booking.heading')} - {booking.id}
          </h3>
        </div>
        <div className="space-y-4 p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.fullName')}: </strong>{booking.fullName}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.email')}: </strong>{booking.email}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.contact')}: </strong>{formatContact(booking.contact)}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.city')}: </strong>{booking.city}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.country')}: </strong>{booking.country}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.adults')}: </strong>{booking.adults}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.children')}: </strong>{booking.children}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.startDate')}: </strong>{new Date(booking.startDate).toLocaleDateString()}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.endDate')}: </strong>{booking.endDate ? new Date(booking.endDate).toLocaleDateString() : '-'}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.totalPrice')}: </strong>{booking.currency === "usd" ? "$" : "฿"} {booking.totalPrice}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.paymentMode')}: </strong>{booking.paymentMode === 'online' ? t('online') : booking.paymentMode === 'offline' ? t('offline') : booking.paymentMode}</p>
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><strong>{t('view-booking.status')}: </strong>{booking.status === 'booked' ? t('bookings.status.booked') : booking.status === 'cancelled' ? t('bookings.status.cancelled') : booking.status}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <strong className="text-sm uppercase tracking-[0.1em] text-slate-500">{t('view-booking.packagesId')}</strong>
            <div className="mt-3 overflow-x-auto">
              <table className='min-w-full divide-y divide-slate-200'>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {
                    booking.packagesId.map((Package, i) => (
                      <tr key={`${Package.id}-${i}`}>
                        <td className="px-3 py-2 text-slate-700">{Package.id}</td>
                        <td className="px-3 py-2 text-slate-700">{renderCategory(Package.pkg)}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>

          {(booking.userId || booking.userId === 0) && (
            <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <strong>{t('view-booking.userId')}: </strong>{(booking.userId === 0) ? t('view-booking.guest') : booking.userId}
            </p>
          )}
        </div>
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 text-sm text-slate-500">
          {t('view-booking.createdAt')}:{" "}
          {new Date(booking.createdAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

export default ViewBooking;
